import React, { useEffect, useRef, useState } from 'react';
import Slider from 'react-slick';
import classNames from 'classnames';

import { backgroundClassMap, carouselSettings } from './constants';
import { NextArrow, PrevArrow, ReviewCard } from './components';
import { ReviewsProps } from './ReviewsProps';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useSwipe from '@utils/hooks/useSwipe';
import ProlongledOrangeOval from '@static/images/figures/orange-prolonged-oval.svg';
import YellowIrregularCircle from '@static/images/figures/yellow-irregular-circle.svg';
import BlueOval from '@static/images/figures/blue-oval.svg';

import * as styles from './Reviews.module.scss';

export const Reviews: React.VFC<ReviewsProps> = ({
	reviews,
	title,
	titleClass = 'font-size-medium',
	subTitleClass = 'font-size-md mt-2',
	maxWidth = '1100px',
	subtitle = '',
	bg = 'gray',
	variant = 'default',
	carouselStyles = {},
	shapesDecoration = false,
}) => {
	const sliderRef = useRef<Slider | null>(null);
	const [currentReview, setCurrentReview] = useState(0);
	const [isSwiped, onTouchStart, onTouchMove, onTouchEnd] = useSwipe(30);
	const [shouldAutoplay, setShouldAutoplay] = useState(true);
	const isOutlined = variant === 'outlined';

	const isMobile = useMediaQuery('md');

	useEffect(() => {
		if (isSwiped && isMobile) setShouldAutoplay(false);
	}, [isSwiped]);

	return (
		<section
			className={classNames(styles.paddingContainer, backgroundClassMap[bg])}
		>
			<div className={styles.container} style={{ maxWidth }}>
				<div className={styles.header}>
					<h2 className={titleClass}>{title}</h2>
					<p className={subTitleClass}>{subtitle}</p>
				</div>
				<div
					onTouchStart={isSwiped ? null : onTouchStart}
					onTouchMove={isSwiped ? null : onTouchMove}
					onTouchEnd={isSwiped ? null : onTouchEnd}
					className={styles.carousel}
					style={carouselStyles}
				>
					<Slider
						{...carouselSettings}
						autoplay={shouldAutoplay}
						className={classNames(
							styles.sliderWrapper,
							isOutlined && styles.outlined
						)}
						appendDots={(dots) => (
							<div style={{ bottom: '-40px' }}>
								<ul className="d-flex justify-content-center align-items-center">
									{dots}
								</ul>
							</div>
						)}
						customPaging={(index) => (
							<div
								className={classNames(
									styles.dotsWrapper,
									'd-flex justify-content-center align-items-center'
								)}
							>
								<div
									className={classNames(
										styles.dots,
										currentReview === index && styles.active
									)}
								/>
							</div>
						)}
						beforeChange={(_, next) => {
							setCurrentReview(next);
						}}
						ref={sliderRef}
						nextArrow={
							<NextArrow color={bg === 'gradient' ? 'white' : 'black'} />
						}
						prevArrow={
							<PrevArrow color={bg === 'gradient' ? 'white' : 'black'} />
						}
					>
						{reviews.map((review) => (
							<ReviewCard
								key={`${review.title} + ${review.reviewerName}`}
								linkVariant={variant}
								alignment={variant === 'outlined' ? 'center' : 'default'}
								{...review}
							/>
						))}
					</Slider>
					{shapesDecoration && (
						<div className={styles.decorations}>
							<img
								className={styles.orangeShape}
								src={ProlongledOrangeOval}
								alt="Orange shape"
							/>
							<img
								className={styles.blueShape}
								src={BlueOval}
								alt="Blue shape"
							/>
							<img
								className={styles.yellowShape}
								src={YellowIrregularCircle}
								alt="Yellow shape"
							/>
						</div>
					)}
				</div>
			</div>
		</section>
	);
};
